import { useCallback, useState } from 'react';
import { CARD_CATEGORY_VALUES } from '../constants/BaseballCardConstants';

export interface UseCardCategorySelectionReturn {
  /** The set of currently-selected category values (a subset of CARD_CATEGORY_VALUES). */
  selectedCategories: Set<string>;
  /** Toggles one category value in/out of the selection. */
  toggleCategory: (value: string) => void;
}

/**
 * Owns which input-document categories the org-card generation run should read. Defaults to
 * all categories selected (opt-out model, mirroring the employee-selection model). Kept out of
 * the Step 5 component so that step stays presentation-only per the feature's separation of
 * concerns. `toggleCategory` returns a fresh Set each call to preserve React state identity.
 */
export const useCardCategorySelection = (): UseCardCategorySelectionReturn => {
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(() => new Set(CARD_CATEGORY_VALUES));

  const toggleCategory = useCallback((value: string) => {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  }, []);

  return { selectedCategories, toggleCategory };
};
